import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  TablePagination,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Skeleton
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import SearchIcon from '@mui/icons-material/Search';
import { useNoticiaActions } from '../../states/noticias/noticia.actions';
import { INoticia } from '../../commons/types/noticia';
import NoticiaCardFixed from './NoticiaCardFixed';
import { useTranslation } from '../../commons/useTranslasion';

const NoticiaPage: React.FC = () => {
  const { translate } = useTranslation();
  const noticiaActions = useNoticiaActions();
  
  // Estados
  const [noticias, setNoticias] = useState<INoticia[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [categoria, setCategoria] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(15);
  const [viewMode] = useState<'grid'>('grid');
  const [categorias, setCategorias] = useState<string[]>([]);

  const loadNoticias = async () => {
    setLoading(true);
    try {
      const res = await noticiaActions.filter({});
      
      if (res && res.itens) {
        setNoticias(res.itens);
        setTotalItems(res.itens.length);
        
        if (res.itens.length > 0) {
          const uniqueCategorias = Array.from(new Set(res.itens
            .map((noticia: INoticia) => noticia.categoria)
            .filter(Boolean)));
          setCategorias(uniqueCategorias as string[]);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar notícias:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNoticias();
  }, []);

  const filteredNoticias = noticias.filter(noticia => {
    const matchesTerm = !searchTerm || 
      (noticia.titulo?.toLowerCase().includes(searchTerm.toLowerCase()) || 
       noticia.categoria?.toLowerCase().includes(searchTerm.toLowerCase()));
       
    const matchesCategoria = !categoria || noticia.categoria === categoria;
    
    return matchesTerm && matchesCategoria;
  });
  
  const filteredTotal = filteredNoticias.length;
  
  const paginatedNoticias = filteredNoticias.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleCategoriaChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setCategoria(event.target.value as string);
  };

  const handleSearch = () => {};

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };



  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
        {translate('noticias')}
      </Typography>
      
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              label={translate('buscarNoticias')}
              variant="outlined"
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyPress={handleKeyPress}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleSearch} edge="end">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="categoria-select-label">{translate('categoria')}</InputLabel>
              <Select
                labelId="categoria-select-label"
                value={categoria}
                onChange={handleCategoriaChange as any}
                label={translate('categoria')}
              >
                <MenuItem value="">
                  <em>{translate('todasCategorias')}</em>
                </MenuItem>
                {categorias.map((cat) => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {loading ? (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <CircularProgress />
          </Box>
          <Grid container spacing={2}>
            {Array.from(new Array(10)).map((_, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4, xl: 2.4 }} key={index}>
                <Box sx={{ height: '100%', mb: 2 }}>
                  <Skeleton variant="rectangular" height={180} width="100%" />
                  <Skeleton variant="text" height={40} width="80%" sx={{ mt: 1 }} />
                  <Skeleton variant="text" height={20} width="60%" />
                </Box>
              </Grid>
            ))}
          </Grid>
        </>
      ) : (
        <>
          <Grid container spacing={2}>
            {paginatedNoticias.map((noticia) => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4, xl: 2.4 }} key={noticia.id}>
                <Box sx={{ height: '100%' }}>
                  <NoticiaCardFixed noticia={noticia} />
                </Box>
              </Grid>
            ))}
          </Grid>
          
          {filteredTotal === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography>{translate('nenhumaNoticiaEncontrada')}</Typography>
            </Box>
          )}
        </>
      )}

      <TablePagination
        component="div"
        count={filteredTotal}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[15, 30, 50, 100]}
        labelRowsPerPage={`${translate('itensPorPagina')}:`}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} ${translate('de')} ${count !== -1 ? count : `${translate('maisDe')} ${to}`}`
        }
      />
    </Container>
  );
};

export default NoticiaPage;